import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Approval_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ContentTable } from 'pdfmake/interfaces';
import { useTranslation } from 'react-i18next';
import type { GetStandardFormFieldLabel } from 'src/hooks/forms/useFormCustomisation';

import { useLazyGetAcceptancesByParentRiskId } from '@/hooks/queries';
import { toLocalDate } from '@/utils/dateUtils';
import { createTable, tableHeaders } from '@/utils/pdf/table';

/**
 * The risk PDF's acceptance table builds its rows straight from the query rather
 * than through `pages/acceptances/useLabelledFields`, so it never saw the
 * register's derived statuses — it printed the raw DB `Status`, showing neither
 * "Pending approval" for an in-flight change request nor, after COI-916, that a
 * deletion is awaiting approval. Both are derived here from the same
 * `changeRequests` relation the register uses, so the PDF agrees with the screen.
 */
const useAcceptancesExportTable = (
  riskId: string,
  getStandardFieldLabel: GetStandardFormFieldLabel
): [() => Promise<ContentTable>, boolean] => {
  const { getLabel: getStatusLabel } = useRating('acceptance_status');
  const { t } = useTranslation(['common']);
  const [getAcceptances, getAcceptancesResult] =
    useLazyGetAcceptancesByParentRiskId();

  const acceptanceStatusLabel = (acceptance: {
    Status: string;
    changeRequests: { ChangeRequestStatus: string; Type: string }[];
  }): string => {
    const pending = acceptance.changeRequests.filter(
      (changeRequest) =>
        changeRequest.ChangeRequestStatus === Approval_Status_Enum.Pending
    );
    if (pending.some((changeRequest) => changeRequest.Type === 'delete')) {
      return `${getStatusLabel('pending_approval')} (${t('approvals.pending_deletion_status')})`;
    }

    return getStatusLabel(
      pending.length > 0 ? 'pending_approval' : acceptance.Status
    );
  };

  const createExportTable = async () => {
    const { data: appetiteData } = await getAcceptances({ riskId });
    const acceptanceTableData = (appetiteData?.acceptance ?? []).map((i) => [
      i.Title,
      i.Details ?? '',
      toLocalDate(i.DateAcceptedFrom),
      toLocalDate(i.DateAcceptedTo),
      acceptanceStatusLabel(i),
    ]);

    return createTable({
      widths: '*',
      body: [
        tableHeaders([
          getStandardFieldLabel('acceptance', 'Title'),
          getStandardFieldLabel('acceptance', 'Details'),
          getStandardFieldLabel('acceptance', 'DateAcceptedFrom'),
          getStandardFieldLabel('acceptance', 'DateAcceptedTo'),
          getStandardFieldLabel('acceptance', 'Status'),
        ]),
        ...acceptanceTableData,
      ],
    });
  };

  return [createExportTable, getAcceptancesResult.loading];
};

export default useAcceptancesExportTable;
